import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ScrEnvironment } from '../../environments/environment';
import { TransferResult, TransferAccept, ShortCodeHistory, ReportSuspiciousActivityRequest, SuspiciousActivityResponse, SuspiciousActivityListResponse, UpdateSuspiciousActivityRequest, LookupResult } from '../models/shortcodes.model';
import { ScrBaseResponse, IScrBaseResponse, ScrBaseService, ScrMessage } from './base.service';
import { pdfDownload, WITHCRED, WithCredNoCache, octetStreamDownload, pdfDownloadNoResponse } from './authentication.service';
import { ScrServiceManager } from './servicemanager.service';
import { BillingOrder } from '../models/billingorder.model';
import { SelectType } from '../models/constants';
import { BadgeData } from "src/app/models/badge.model";
import { DiscountSummary } from '../models/discount.model';
import { BadgeService } from './badge.service';
import { ConfigService } from './config.service';
import { Lease, LeaseBilling, LeaseSummary, LeaseSummaryResponse, LeasedCodeDetail } from '../models/lease.model';
import { MonthlyInvoice } from '../models/monthlyinvoice.model';
import { CodeAndHash } from '../models/lease.model';

export interface MonthlyInvoiceData {
  invoices: MonthlyInvoice[];
}

export interface MonthlyInvoiceDataWithPaging extends MonthlyInvoiceData {
  invoices: MonthlyInvoice[];
  page: number;
  limit: number;
  totalCount: number;
  unpaidCount: number;
}


export interface MonthlyInvoiceListResponse extends IScrBaseResponse {
  data: MonthlyInvoiceDataWithPaging;
}

export interface MonthlyInvoiceResponse extends IScrBaseResponse {
  data: MonthlyInvoiceData;
}


export class ShortCodeLookupResponse extends ScrBaseResponse {
  // return is a combination of Lease + aggregatorName + scStatus.
  data!: LookupResult;
}

export class ShortCodeHistoryResult extends ScrBaseResponse {
  codes!: ShortCodeHistory[];
  scStatus!: string;
}


export class ShortCodeHistoryResponse extends ScrBaseResponse {
  data!: ShortCodeHistoryResult;
}



export class CartRandomCodeResponse extends ScrBaseResponse {
  data!: { results: number[] };
}

export class CartSelectCodeResponse extends ScrBaseResponse {
  data!: { results: number[], similar: number[] };
}

export class CartCodesAvailableStatusResponse extends ScrBaseResponse {
  data!: { codesAvailable: number[], codesUnavailable: number[] };
}

export interface CartUpdatePricingResponse extends IScrBaseResponse {
  order: BillingOrder;
  hash: string;
  codesUnavailable?: number[];
  additions?: number[];
  promotionsApplied?: DiscountSummary[];
}

export interface GetOrderResponse extends IScrBaseResponse {
  order: BillingOrder;
  orderCount?: number;
  renewalDeadLineStr?: string;
  renewalAfterDeadline?: boolean;
  monBillStartDateStr?: string;
  allMBDiscountsNotApplied?: boolean
}

export interface GetOrdersResponse extends IScrBaseResponse {
  order: BillingOrder[];
  orderCount?: number;
}

export interface GetOrderForRenewResponse extends GetOrderResponse {
  monthlyBillingEligible: boolean,
  renew1Plus12: boolean
}

export class GetDiscountSummaryResponse extends ScrBaseResponse {
  constructor(public discounts: DiscountSummary[], status: string, messages: ScrMessage[]) {
    super(status, messages);
  }
}


export interface SortSettings {
  direction: string; // ASC DESC
  property: string;
  ignoreCase: boolean;
  nullHandling: string;
}

export interface Pageable {
  page: number;
  size: number;
  sort: Record<string,SortSettings>;
}

export interface ShortCodeListItem {
  shortCode: number;
  available: boolean;
  coolName: string | undefined;
}
export interface ShortCodeList {
  total: number;
  content: ShortCodeListItem[];
}
export interface MemorableCodeList {
  shortCodeList: ShortCodeList;
}
export interface GetMemorableCodesResponse extends IScrBaseResponse {
  data: MemorableCodeList;
}

/**
 * A minimally typed response to make the observer work.i
 */
export interface CancelValidationResponse {

  isError?: boolean;
  information?: string;
  message?: string;
  messages?: ScrMessage[] | undefined;

}

export interface CancelValidation extends IScrBaseResponse {

  results:CancelValidationResponse;
}

/**
 * A minimally typed response to make the observer work.
 */
export interface ShortCodeLeaseDetailsResponse extends IScrBaseResponse {
  // we can ask for more than one short code at a time so returns
  // are an array.
  results: LeasedCodeDetail[];
}





export interface LeaseApplicationResponse extends IScrBaseResponse {

  leasedCodeDetail?: Lease;

}


export interface LeaseBillingResponse extends IScrBaseResponse {

  leasedCodeDetail?: LeaseBilling;

}

export interface AdditionalMbOptData {
  monthlyInvoiceId: number;
  invoiceDate: string;
}


export interface MbOptOutResponse extends IScrBaseResponse {
  additionalData: AdditionalMbOptData;
}


export interface UpdatePricingArgs {
  order: BillingOrder;
  applyPromotionCode?: string;
  removePromotionCode?: string;
  additionalRandomCodes?: number | CodeAndHash[];
  additionalSelectCodes?: number[];
  correspondingSearchString?: string;
  validateAvailability?: boolean;
  addOneMonthLease?: boolean;
  singleRedeemCodeMode?: boolean;
  leaseByLeaseMode?: boolean;
}



@Injectable({
  providedIn: 'root'
})
export class ShortCodesService extends ScrBaseService {

  scPurchasePayLater = false;

  constructor(
    serviceManager: ScrServiceManager,
    private environment: ScrEnvironment,

    private badgeService: BadgeService,
    private http: HttpClient,
    private configService: ConfigService) {

    super(serviceManager);


  }

  getShortCodesList(page: string, pageSize: string): Observable<LeaseSummaryResponse> {
    const url = `${this.environment.base}/secure/leasedcode?page=${page}&size=${pageSize}`;
    return this.http.get<LeaseSummaryResponse>(url, WithCredNoCache);
  }

  getCustomQueryShortCodeList(page: string, pageSize: string, customQuery: 'bulkrenew' | 'needsaction' | 'linkedaccounts' | 'linkedaccounts-needsaction'): Observable<LeaseSummaryResponse> {
    const url = `${this.environment.base}/secure/leasedcode?page=${page}&size=${pageSize}&cannedQuery=${customQuery}`;
    return this.http.get<LeaseSummaryResponse>(url, WithCredNoCache);
  }

  /**
   * Get the leased code details.
   * @param scId - short code id.
   * @returns returns the details.
   */
  getLeasedCodeDetail(scId: number): Observable<ShortCodeLeaseDetailsResponse> {
    const url = `${this.environment.base}/secure/leasedcode/${scId}`;
    return this.http.get<ShortCodeLeaseDetailsResponse>(url, WithCredNoCache);
  }

  /**
   * Get details for a list of codes.  The short code list page
   * needs to restore its state and many items could be expanded at that time.
   * @param scIds - array of short code IDs
   * @returns 
   */
  getMultiLeasedCodeDetail(scIds: string[], doNotErrorIfMissing = false): Observable<ShortCodeLeaseDetailsResponse> {
    const url = `${this.environment.base}/secure/leasedcode/${scIds}?doNotErrorIfMissing=${doNotErrorIfMissing}`;
    return this.http.get<ShortCodeLeaseDetailsResponse>(url, WithCredNoCache);
  }

  validateCancel(scId:number, leaseStatus:string, orderId: number): Observable<CancelValidation> {
    const url = `${this.environment.base}/secure/leasedcode/cancel/${scId}`;
   const body = { leaseStatus :leaseStatus, orderId :orderId};
    return this.http.post<CancelValidation>(url,body, WITHCRED);
  }
  cancelLeastShortCode(action:string, scId:number, orderId:number, leaseStatus: string, cancelOption:string ): Observable<IScrBaseResponse>{
      const url = `${this.environment.base}/fct/leaseActions`;
       const body = this.functionUrlBody(action, {scId: scId, cancelOption: cancelOption, leaseStatus: leaseStatus, orderId: orderId});
      return this.http.post<IScrBaseResponse>(url, body, WITHCRED);
  }

  

  getMonthlyInvoiceList(page: string, pageSize: string, detailPageSize: string, searchInvoiceId?: number): Observable<MonthlyInvoiceListResponse> {
    let url;
    if (searchInvoiceId)
      url = `${this.environment.base}/secure/monthlyinvoice/` + searchInvoiceId.toString();
    else
      url = `${this.environment.base}/secure/monthlyinvoice?page=${page}&size=${pageSize}&detailSize=${detailPageSize}`;
    return this.http.get<MonthlyInvoiceListResponse>(url, WithCredNoCache);
  }

  getMonthlyInvoiceDetail(monthlyInvoiceId: number): Observable<MonthlyInvoiceResponse> {
    const url = `${this.environment.base}/secure/monthlyinvoice/` + monthlyInvoiceId;
    return this.http.get<MonthlyInvoiceResponse>(url, WithCredNoCache);
  }


  scOptAutoRenew(scId: number, optIn: boolean): Observable<IScrBaseResponse> {
    const url = `${this.environment.base}/secure/leasedcode/autorenew/${scId}`;
    const body = { optIn };
    return this.http.put<IScrBaseResponse>(url, body, WITHCRED);
  }

  shortCodeLookup(scId: string): Observable<ShortCodeLookupResponse> {
    const url = `${this.environment.base}/secure/codemanagement/${scId}`;
    return this.http.get<ShortCodeLookupResponse>(url, WithCredNoCache);
  }

  shortCodeHistory(scId: string): Observable<ShortCodeHistoryResponse> {
    const url = `${this.environment.base}/secure/codemanagement/history/${scId}`;
    return this.http.get<ShortCodeHistoryResponse>(url, WithCredNoCache);
  }



  shortCodeHistoryDownload(scId: string): Observable<HttpResponse<Blob>> {
    const url = `${this.environment.base}/secure/codemanagement/history/download/${scId}`;
    return this.http.get<HttpResponse<Blob>>(url, octetStreamDownload);
  }


  getIncomingAndOutgoingTransfers(): Observable<TransferResult> {
    const url = `${this.environment.base}/secure/transfer`;
    return this.http.get<TransferResult>(url, WithCredNoCache);
  }

  getIncomingTransferList(page: number, limit: number): Observable<TransferResult> {

    const url = `${this.environment.base}/secure/transfer/incomingtransfer?page=${page}&limit=${limit}`;

     return this.http.get<TransferResult>(url, WithCredNoCache);
  }

  getOutgoingTransferList(page: number, limit: number, scid: number | undefined): Observable<TransferResult> {
    let url;
    if(scid)
     url = `${this.environment.base}/secure/transfer/outgoingtransfer?page=${page}&limit=${limit}&scid=${scid}`;
    else
     url = `${this.environment.base}/secure/transfer/outgoingtransfer?page=${page}&limit=${limit}`;
     return this.http.get<TransferResult>(url, WithCredNoCache);
  }


  downloadMonthlyShortCodeStatement(scId: number): Observable<HttpResponse<Blob>> {
    const url = `${this.environment.base}/secure/leasedcode/monthlystatement/${scId}`;
    return this.http.get<HttpResponse<Blob>>(url, pdfDownload);
  }

  // Cart APIs

  getSelectCode(shortCode: string): Observable<CartSelectCodeResponse> {
    const url = `${this.environment.base}/secure/billingorder/search`;
    const body = { searchType: SelectType.Selected, selectSearch: shortCode };
    return this.http.post<CartSelectCodeResponse>(url, body, WITHCRED);
  }

  getPendingOrderCodesAvailability(order: BillingOrder): Observable<CartCodesAvailableStatusResponse> {
    const url = `${this.environment.base}/secure/billingorder/validate`;
    const body = { order };
    return this.http.post<CartCodesAvailableStatusResponse>(url, body, WITHCRED);
  }

  updatePricing(args: UpdatePricingArgs): Observable<CartUpdatePricingResponse> {
    const url = `${this.environment.base}/secure/billingorder/updatepricing`;
    return this.http.post<CartUpdatePricingResponse>(url, args, WITHCRED);
  }

  initiateTransfer(scId: number, toAccountId: number): Observable<ScrBaseResponse> {
    const url = `${this.environment.base}/secure/transfer/initiate`;
    const payload = { scId, toAccountId };
    return this.http.post<ScrBaseResponse>(url, payload, WITHCRED).pipe(tap((rsp:ScrBaseResponse) => {
      if ((rsp.status === 'success') && (rsp)) {
        this.badgeService.updateLocally((data: BadgeData) => {
          data.transfersCount = data.transfersCount + 1;
          return data;
        });
      }
    }));


  }

  cancelTransfer(transferId: number): Observable<ScrBaseResponse> {
    const url = `${this.environment.base}/secure/transfer/cancel`;
    return this.http.post<ScrBaseResponse>(url, { transferId }, WITHCRED).pipe(tap((rsp:ScrBaseResponse) => {
      if ((rsp.status === 'success') && (rsp)) {
        this.badgeService.updateLocally((data: BadgeData) => {
          data.transfersCount = data.transfersCount - 1;
          return data;
        });
      }
    }));
  }

  acceptTransfer(accept: TransferAccept): Observable<IScrBaseResponse> {
    const url = `${this.environment.base}/fct/transfers/accept`;
    return this.http.post<IScrBaseResponse>(url, accept, WITHCRED);
  }

  getMbOptOut(leaseId: number): Observable<IScrBaseResponse> {
    const url = `${this.environment.base}/secure/leasedcode/mboptoutinfo/${leaseId}`;
    return this.http.get<TransferResult>(url, WithCredNoCache);
  }
  
  mbOptOut(leaseId: number, cancelSc: boolean): Observable<MbOptOutResponse> {
    const url = `${this.environment.base}/fct/leaseActions`;
    const body = this.functionUrlBody('leaseMbOptOut', {leaseId, cancelSc});
    return this.http.post<MbOptOutResponse>(url, body, WITHCRED);
  }

  declineTransfer(transferId: number): Observable<IScrBaseResponse> {
    const url = `${this.environment.base}/secure/transfer/decline`;
    return this.http.post<IScrBaseResponse>(url, { transferId }, WITHCRED);
  }


  getOrder(orderId: number, scId?: number): Observable<GetOrderResponse> {
    let url = `${this.environment.base}/secure/billingorder/${orderId}`;

    if (scId)
      url += `?scId=${scId}`;

    return this.http.get<GetOrderResponse>(url, WithCredNoCache);
  }

  getOrders(orders: number[]): Observable<GetOrdersResponse> {
    const url = `${this.environment.base}/secure/billingorder/orders`;
    return this.http.post<GetOrdersResponse>(url, { orderIds: orders }, WithCredNoCache);
  }
  
  getRenewOrder(scId: number): Observable<GetOrderForRenewResponse> {
    const url = `${this.environment.base}/secure/billingorder/renew/${scId}`;
    return this.http.get<GetOrderForRenewResponse>(url, WithCredNoCache);
  }

  getIndividualReceiptPdf(monthlyInvoiceId: number, scId: number): Observable<Blob> {
    const url = `${this.environment.base}/secure/leasedcode/receipt/${monthlyInvoiceId}/sc/${scId}`;
    return this.http.get<Blob>(url, pdfDownloadNoResponse);
  }

  /**
   * Download a receipt PDF for the full monthly invoice, payment or oder.
   * @param orderMonthlyInvoiceOrPaymentId - identifier for order, monthly invoice, or payment.
   * @returns Blob containing a PDF.
   */
  getReceiptPdf(orderMonthlyInvoiceOrPaymentId: number): Observable<Blob> {
    const url = `${this.environment.base}/secure/document/receipt/${orderMonthlyInvoiceOrPaymentId}`;
    return this.http.get<Blob>(url, pdfDownloadNoResponse);
  }

  getReceiptPdfWithResponse(orderMonthlyInvoiceOrPaymentId: number): Observable<HttpResponse<Blob>> {
    const url = `${this.environment.base}/secure/document/receipt/${orderMonthlyInvoiceOrPaymentId}`;
    return this.http.get<HttpResponse<Blob>>(url, pdfDownload);
  }


  getInvoicePdf(orderId: number): Observable<Blob> {
    const url = `${this.environment.base}/secure/document/invoice/${orderId}`;
    return this.http.get<Blob>(url, pdfDownloadNoResponse);
  }

  getStatementPdf(orderId: number): Observable<Blob> {
    const url = `${this.environment.base}/secure/document/statement/${orderId}`;
    return this.http.get<Blob>(url, pdfDownloadNoResponse);
  }

  suppressEmail(shortCode: LeaseSummary): Observable<IScrBaseResponse> {
    // When suppressEmail is checked-unchecked user has already chosen to opt - out so set optin to false
    // default of suppress email is always false.
    const expiryFlag = shortCode.details?.lease.suppressExpiryEmail ? shortCode.details.lease.suppressExpiryEmail : false;
    const url = `${this.environment.base}/secure/leasedcode/${shortCode.scId}`;
    const body = { optIn: false, suppressExpiryEmail: expiryFlag };
    return this.http.patch<IScrBaseResponse>(url, body, WITHCRED);
  }

  getApplication(scId: number): Observable<LeaseApplicationResponse> {
    const url = `${this.environment.base}/secure/leasedcode/application/${scId}`;
    return this.http.get<LeaseApplicationResponse>(url, WithCredNoCache);
  }

  getBillingData(scId: number): Observable<LeaseBillingResponse> {
    const url = `${this.environment.base}/secure/leasedcode/billing/${scId}`;
    return this.http.get<LeaseBillingResponse>(url, WithCredNoCache);
  }

  patchApplication(scId: number, lease: Lease, isInvoiceUpdate: boolean): Observable<IScrBaseResponse> {
    const url = `${this.environment.base}/secure/leasedcode/application/${scId}`;
    const body = {
      lease,
      isInvoiceUpdate
    };
    const strBody = JSON.stringify(body, ScrBaseService.phoneReplacer);
    return this.http.patch<IScrBaseResponse>(url, strBody, WITHCRED);
  }
  patchEditBillingApplication(scId: number, body: Partial<LeaseBilling>): Observable<IScrBaseResponse> {
    const url = `${this.environment.base}/secure/leasedcode/billing/${scId}`;
    const strBody = JSON.stringify(body, ScrBaseService.phoneReplacer);
    return this.http.patch<IScrBaseResponse>(url, strBody, WITHCRED);
  }

  /**
   * Download a statement indicating that the short code was paid for.
   * @param scId - short code
   * @param orderId - if provided, pull the lease associated with this order.  Otherwise, the latest lease.
   * 
   * @returns observable of PDF document blob.
   */
  getTransferDoc(scId: number, orderId?: number): Observable<HttpResponse<Blob>> {
    let url = `${this.environment.base}/secure/leasedcode/transferstatement?scId=${scId}`;
    if (orderId) {
      url = url + '&orderId=' + orderId;
    }
    return this.http.get<HttpResponse<Blob>>(url, pdfDownload);
  }

  updatePurchaseOrder(orderId: string | number, poNumber: number | string): Observable<ScrBaseResponse> {
    const url = `${this.environment.base}/secure/billingorder/update/${orderId}`;
    return this.http.patch<ScrBaseResponse>(url, { poNumber }, WITHCRED);
  }

  initiateBulkRenew(leaseIds: number[]): Observable<GetOrderForRenewResponse> {
    const url = `${this.environment.base}/secure/billingorder/bulkrenew`;
    const body = {
      leaseIds
    };
    return this.http.post<GetOrderForRenewResponse>(url, body, WITHCRED);
  }

  getMemorableCodes(typeOfCodes: string, startsWith?: string, coolNameRange?: string, range?: { fromRange: string, toRange: string }): Observable<GetMemorableCodesResponse> {
    let url;
    if (startsWith) {
      url = `${this.environment.base}/secure/billingorder/shortcodes/available?filterOption=${typeOfCodes}&beginsWith=${startsWith}&page=1&size=100`;
    } else if (coolNameRange) {
      url = `${this.environment.base}/secure/billingorder/shortcodes/available?filterOption=${typeOfCodes}&nameGroup=${coolNameRange}&page=1&size=100`;
    } else if (range) {
      url = `${this.environment.base}/secure/billingorder/shortcodes/available?fromShortCode=${range.fromRange}&toShortCode=${range.toRange}&page=1&size=100`;
    } else {
      url = `${this.environment.base}/secure/billingorder/shortcodes/available?filterOption=${typeOfCodes}&page=1&size=100`;
    }
    return this.http.get<GetMemorableCodesResponse>(url, WithCredNoCache);
  }

  submitSuspiciousActivity(activity: ReportSuspiciousActivityRequest): Observable<SuspiciousActivityResponse> {
    const url = `${this.environment.base}/secure2/industryreports/suspiciousactivity`;
    return this.http.post<SuspiciousActivityResponse>(url, activity, WITHCRED);
  }

  updateSuspiciousActivity(activity: UpdateSuspiciousActivityRequest, activityId: number): Observable<SuspiciousActivityResponse> {
    const url = `${this.environment.base}/secure2/industryreports/suspiciousactivity/${activityId}`;
    return this.http.put<SuspiciousActivityResponse>(url, activity, WITHCRED);
  }

  getSuspiciousActivities(): Observable<SuspiciousActivityListResponse> {
    const url = `${this.environment.base}/secure2/industryreports/suspiciousactivities`;
    return this.http.get<SuspiciousActivityListResponse>(url, WITHCRED);
  }

}
